// =============================================================================
// features/conversations/components/AttachToDocumentsDialog.tsx — "Anexar aos
// documentos" a mídia recebida/enviada no live chat (F46-S11).
//
// docs/planejamento-documentos-anexos.md §6.1. O arquivo JÁ existe no storage
// (mesmo objeto da mensagem) — este diálogo só pede nome de exibição e tipo,
// nunca refaz upload.
//
// Padrão reusado (nota do slot: "não existe componente <Modal> canônico —
// reuse o que já existe, não invente um terceiro padrão"):
//   - Portal + overlay: features/documents/components/DocumentUploadDialog.tsx
//   - Foco preso: features/sales/components/useFocusTrap.ts
//
// Erros:
//   - AttachMediaTooOldError (422, mídia anterior a 04/09/2026): falha
//     PERMANENTE — sem botão de retry, só fechar.
//   - AttachAlreadyDoneError (409): a mensagem já virou documento — fecha
//     informando, sem reenviar.
//   - Qualquer outro erro: mensagem + permite tentar de novo (nada foi
//     perdido — o formulário permanece preenchido).
// =============================================================================

import * as React from 'react';
import { createPortal } from 'react-dom';

import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Select } from '../../../components/ui/Select';
import { cn } from '../../../lib/cn';
import { useFocusTrap } from '../../sales/components/useFocusTrap';
import {
  AttachAlreadyDoneError,
  AttachMediaTooOldError,
  useAttachMediaAsDocument,
  useDocumentTypesCatalog,
} from '../hooks/useAttachMediaAsDocument';

export interface AttachToDocumentsDialogProps {
  open: boolean;
  onClose: () => void;
  conversationId: string;
  leadId: string;
  messageId: string;
  /** Nome pré-preenchido (F46-S11: "o agente recebe arquivo com nome
   * qualquer e precisa organizar"). Editável; vazio cai no fallback do
   * servidor para `original_filename` (F46-S31). */
  defaultDisplayName: string;
  onAttached?: (documentId: string) => void;
}

export function AttachToDocumentsDialog({
  open,
  onClose,
  conversationId,
  leadId,
  messageId,
  defaultDisplayName,
  onAttached,
}: AttachToDocumentsDialogProps): React.JSX.Element | null {
  const panelRef = React.useRef<HTMLDivElement>(null);
  const [documentTypeId, setDocumentTypeId] = React.useState('');
  const [displayName, setDisplayName] = React.useState(defaultDisplayName);

  const { data: types, isLoading: typesLoading, isError: typesError } = useDocumentTypesCatalog();
  const mutation = useAttachMediaAsDocument();

  // Reseta o formulário a cada abertura — inclusive quando o mesmo diálogo é
  // reaberto para OUTRA mensagem (defaultDisplayName muda com a mensagem).
  React.useEffect(() => {
    if (open) {
      setDocumentTypeId('');
      setDisplayName(defaultDisplayName);
      mutation.reset();
    }
    // `mutation` (objeto do useMutation) muda de referência a cada render —
    // deliberadamente fora das deps: só o ciclo de abertura/mensagem deve
    // resetar o formulário, nunca um novo objeto de mutação por si só. Este
    // projeto não tem o plugin `eslint-plugin-react-hooks` configurado
    // (não há `exhaustive-deps` para desabilitar).
  }, [open, defaultDisplayName, messageId]);

  const isPermanentFailure = mutation.error instanceof AttachMediaTooOldError;
  const isAlreadyDone = mutation.error instanceof AttachAlreadyDoneError;

  const handleClose = React.useCallback(() => {
    if (mutation.isPending) return;
    onClose();
  }, [mutation.isPending, onClose]);

  useFocusTrap({ containerRef: panelRef, active: open, onEscape: handleClose });

  if (!open) return null;

  const canSubmit = documentTypeId !== '' && !mutation.isPending && !isPermanentFailure;

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    if (!canSubmit) return;
    mutation.mutate(
      {
        messageId,
        conversationId,
        leadId,
        documentTypeId,
        displayName,
      },
      {
        onSuccess: (document) => {
          onAttached?.(document.id);
          onClose();
        },
      },
    );
  };

  return createPortal(
    <>
      <div
        role="presentation"
        aria-hidden="true"
        className="fixed inset-0 z-[150] bg-[var(--text)]/30 backdrop-blur-[2px]"
        onClick={handleClose}
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="attach-to-documents-title"
        className="fixed left-1/2 top-1/2 z-[160] w-full max-w-[480px] -translate-x-1/2 -translate-y-1/2 rounded-md flex flex-col max-h-[85vh]"
        style={{
          background: 'var(--bg-elev-1)',
          border: '1px solid var(--border)',
          boxShadow: 'var(--elev-5)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-5 border-b border-border-subtle shrink-0">
          <h2
            id="attach-to-documents-title"
            className="font-display font-bold text-ink"
            style={{ fontSize: 'var(--text-xl)', letterSpacing: '-0.03em' }}
          >
            Anexar aos documentos
          </h2>
          <button
            type="button"
            onClick={handleClose}
            disabled={mutation.isPending}
            aria-label="Fechar"
            className={cn(
              'w-8 h-8 flex items-center justify-center rounded-sm text-ink-3',
              'hover:text-ink hover:bg-surface-hover disabled:opacity-40 disabled:cursor-not-allowed',
              'focus-visible:ring-2 focus-visible:ring-azul/20 focus-visible:outline-none',
            )}
          >
            <svg
              viewBox="0 0 20 20"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.6}
              className="w-5 h-5"
            >
              <path d="M5 5l10 10M15 5l-10 10" />
            </svg>
          </button>
        </div>

        <form
          id="attach-to-documents-form"
          onSubmit={handleSubmit}
          noValidate
          className="flex-1 overflow-y-auto px-6 py-5 flex flex-col gap-4"
        >
          <p className="font-sans text-sm text-ink-2">
            O arquivo já está salvo — nada será reenviado. Só organize o nome e o tipo.
          </p>

          <Input
            id="attach-document-display-name"
            label="Nome do documento"
            placeholder="Ex.: RG frente — João Silva"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            disabled={mutation.isPending || isPermanentFailure}
            hint="Em branco, usamos o nome original do arquivo."
          />

          {typesError ? (
            <p
              className="rounded-sm px-3 py-2 font-sans text-sm"
              style={{ color: 'var(--danger)', background: 'var(--danger-bg)' }}
            >
              Não foi possível carregar os tipos de documento.
            </p>
          ) : (
            <Select
              id="attach-document-type"
              label="Tipo de documento"
              required
              placeholder={typesLoading ? 'Carregando…' : 'Selecione o tipo'}
              disabled={typesLoading || mutation.isPending || isPermanentFailure}
              value={documentTypeId}
              onChange={(e) => setDocumentTypeId(e.target.value)}
              options={(types ?? []).map((t) => ({ value: t.id, label: t.label }))}
            />
          )}

          {isPermanentFailure && (
            <div
              role="alert"
              className="rounded-sm px-4 py-3 border-l-[3px]"
              style={{
                borderColor: 'var(--warning)',
                background: 'var(--warning-bg)',
                boxShadow: 'var(--elev-1)',
              }}
            >
              <p className="font-sans font-semibold text-sm" style={{ color: 'var(--warning)' }}>
                Esta mídia não pode virar documento
              </p>
              <p className="font-sans text-xs text-ink-2 mt-1">
                Ela chegou antes do suporte a este recurso e não tem como ser recuperada
                automaticamente. Baixe o arquivo pela bolha e anexe manualmente pela ficha do lead,
                se precisar.
              </p>
            </div>
          )}

          {isAlreadyDone && (
            <div
              role="alert"
              className="rounded-sm px-4 py-3 border-l-[3px]"
              style={{
                borderColor: 'var(--info)',
                background: 'var(--info-bg)',
                boxShadow: 'var(--elev-1)',
              }}
            >
              <p className="font-sans font-semibold text-sm" style={{ color: 'var(--info)' }}>
                Já anexado
              </p>
              <p className="font-sans text-xs text-ink-2 mt-1">
                Esta mídia já virou documento — confira na ficha do lead.
              </p>
            </div>
          )}

          {mutation.isError && !isPermanentFailure && !isAlreadyDone && (
            <div
              role="alert"
              className="rounded-sm px-4 py-3 border-l-[3px]"
              style={{
                borderColor: 'var(--danger)',
                background: 'var(--danger-bg)',
                boxShadow: 'var(--elev-1)',
              }}
            >
              <p className="font-sans font-semibold text-sm text-danger">Erro ao anexar</p>
              <p className="font-sans text-xs text-ink-2 mt-1">{mutation.error.message}</p>
              <p className="font-sans text-xs text-ink-2 mt-1">
                Nada foi perdido — pode tentar novamente.
              </p>
            </div>
          )}
        </form>

        <div className="flex gap-3 px-6 py-5 border-t border-border-subtle shrink-0">
          <Button
            type="button"
            variant="ghost"
            onClick={handleClose}
            disabled={mutation.isPending}
            className="flex-1"
          >
            {isPermanentFailure || isAlreadyDone ? 'Fechar' : 'Cancelar'}
          </Button>
          {!isPermanentFailure && !isAlreadyDone && (
            <Button
              type="submit"
              form="attach-to-documents-form"
              variant="primary"
              disabled={!canSubmit}
              className="flex-1"
            >
              {mutation.isPending ? 'Anexando…' : 'Anexar'}
            </Button>
          )}
        </div>
      </div>
    </>,
    document.body,
  );
}
